import { Directive, NgZone, OnDestroy, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { Subscription } from 'rxjs';

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const EDGES: ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const HANDLE_STYLE_ELEMENT_ID = 'app-resizable-dialog-handle-styles';

function ensureHandleStylesInjected(): void {
  if (document.getElementById(HANDLE_STYLE_ELEMENT_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = HANDLE_STYLE_ELEMENT_ID;
  style.textContent = `
    .p-custom-resize-handle { position: absolute; z-index: 1; }
    .p-custom-resize-n, .p-custom-resize-s { left: 10px; right: 10px; height: 6px; cursor: ns-resize; }
    .p-custom-resize-n { top: -3px; }
    .p-custom-resize-s { bottom: -3px; }
    .p-custom-resize-e, .p-custom-resize-w { top: 10px; bottom: 10px; width: 6px; cursor: ew-resize; }
    .p-custom-resize-e { right: -3px; }
    .p-custom-resize-w { left: -3px; }
    .p-custom-resize-ne, .p-custom-resize-nw, .p-custom-resize-se, .p-custom-resize-sw { width: 14px; height: 14px; }
    .p-custom-resize-ne { top: -3px; right: -3px; cursor: nesw-resize; }
    .p-custom-resize-nw { top: -3px; left: -3px; cursor: nwse-resize; }
    .p-custom-resize-se { bottom: -3px; right: -3px; cursor: nwse-resize; }
    .p-custom-resize-sw { bottom: -3px; left: -3px; cursor: nesw-resize; }
  `;
  document.head.appendChild(style);
}

@Directive({
  selector: '[appResizableDialog]',
  standalone: true
})
export class ResizableDialogDirective implements OnDestroy {
  private dialog = inject(Dialog, { self: true });
  private zone = inject(NgZone);

  private handles: HTMLElement[] = [];
  private endDrag: (() => void) | null = null;
  private subscriptions = new Subscription();

  constructor() {
    ensureHandleStylesInjected();
    this.subscriptions.add(this.dialog.onShow.subscribe(() => this.attachHandles()));
    this.subscriptions.add(this.dialog.onHide.subscribe(() => this.detachHandles()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.detachHandles();
  }

  private attachHandles(): void {
    this.detachHandles();
    const container = this.dialog.container;
    if (!container) {
      return;
    }

    for (const edge of EDGES) {
      const handle = document.createElement('div');
      handle.className = `p-custom-resize-handle p-custom-resize-${edge}`;
      handle.addEventListener('mousedown', (event: MouseEvent) => this.startResize(event, container, edge));
      container.appendChild(handle);
      this.handles.push(handle);
    }
  }

  private detachHandles(): void {
    this.handles.forEach(handle => handle.remove());
    this.handles = [];
    this.endDrag?.();
    this.endDrag = null;
  }

  private startResize(event: MouseEvent, container: HTMLElement, edge: ResizeEdge): void {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.pageX;
    const startY = event.pageY;
    const rect = container.getBoundingClientRect();
    const startWidth = rect.width;
    const startHeight = rect.height;
    const startLeft = rect.left;
    const startTop = rect.top;

    container.style.position = 'fixed';
    container.style.margin = '0';
    container.style.left = `${startLeft}px`;
    container.style.top = `${startTop}px`;
    document.body.classList.add('p-unselectable-text');

    this.zone.runOutsideAngular(() => {
      const onMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.pageX - startX;
        const deltaY = moveEvent.pageY - startY;

        if (edge.includes('e')) {
          container.style.width = `${Math.max(MIN_WIDTH, startWidth + deltaX)}px`;
        }
        if (edge.includes('w')) {
          const newWidth = Math.max(MIN_WIDTH, startWidth - deltaX);
          container.style.width = `${newWidth}px`;
          container.style.left = `${startLeft + (startWidth - newWidth)}px`;
        }
        if (edge.includes('s')) {
          container.style.height = `${Math.max(MIN_HEIGHT, startHeight + deltaY)}px`;
        }
        if (edge.includes('n')) {
          const newHeight = Math.max(MIN_HEIGHT, startHeight - deltaY);
          container.style.height = `${newHeight}px`;
          container.style.top = `${startTop + (startHeight - newHeight)}px`;
        }
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.classList.remove('p-unselectable-text');
        this.endDrag = null;
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      this.endDrag = onMouseUp;
    });
  }
}
